<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ContentLoader from '@/components/content-loader.vue';
import useNotifications from "@/composables/notifications";
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from "@/components/modal/modal-title.vue";
import ModalContent from '@/components/modal/modal-content.vue';
import ModalFooterConfirmation from "@/components/modal/modal-footer-confirmation.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InputSelect from "@/components/input-select.vue";
import TOUGrid from '@/features/tariffs/components/TouGrid.vue';
import { faQuestionCircle, faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { filterQueries, mainQueries } from '@/features/tariffs/queries';
import { useQuery } from '@tanstack/vue-query';
import { Season, PSOShort } from '@/features/tariffs/constants'
import type { TOUProfile, TOUProfileMini } from '@/features/tariffs/types';
import { useSaveTouProfile } from '@/features/tariffs/mutations';

const utilityProvider = ref<number>(0)
const { data: utilityProviderData, isLoading } = useQuery(filterQueries.fetchProviders())

const selectedProvider = computed(() => utilityProviderData.value?.find(x => x.Id == utilityProvider.value))
const profileId = computed(() => selectedProvider.value?.TimeOfUseProfileId ?? 0)
const linkedValue = computed(() => selectedProvider.value?.Linked ?? 0)
const hasSelectedProvider = computed(() => utilityProvider.value > 0)

const { data: touprofileData, isLoading: touLoading } = useQuery(mainQueries.fetchTOUProfileConfig(profileId))
const { mutateAsync } = useSaveTouProfile()
const selectedState = ref(PSOShort.Peak)
const { addSuccess, addError } = useNotifications();

const touData = ref<{
  Summer: TOUProfileMini[],
  Winter: TOUProfileMini[]
}>({
  Summer: [],
  Winter: []
})

watch(touprofileData, (data) => {
  if (!data) return
  const split = splitToSeasons(data)
  touData.value = { Summer: split.Summer, Winter: split.Winter }
})

function splitToSeasons(data: TOUProfile[]) {
  const summer = data.map(p => ({
    Id: p.Id,
    Hour: p.Hour,
    Week: p.Week_l,
    Saturday: p.Saturday_l,
    Sunday: p.Sunday_l,
    TimeOfUseProfileId: p.TimeOfUseProfileId
  }))

  const winter = data.map(p => ({
    Id: p.Id,
    Hour: p.Hour,
    Week: p.Week_h,
    Saturday: p.Saturday_h,
    Sunday: p.Sunday_h,
    TimeOfUseProfileId: p.TimeOfUseProfileId
  }))

  return { Summer: summer, Winter: winter }
}

function mergeSeasons(summer: TOUProfileMini[], winter: TOUProfileMini[]): TOUProfile[] {
  return summer.map((summerItem, index) => {
    const winterItem = winter[index];
    return {
      Id: summerItem.Id,
      Hour: summerItem.Hour,
      Week_l: summerItem.Week,
      Saturday_l: summerItem.Saturday,
      Sunday_l: summerItem.Sunday,
      Week_h: winterItem?.Week ?? '',
      Saturday_h: winterItem?.Saturday ?? '',
      Sunday_h: winterItem?.Sunday ?? '',
      TimeOfUseProfileId: summerItem.TimeOfUseProfileId
    };
  });
}

async function save() {
  const merged = mergeSeasons(touData.value.Summer, touData.value.Winter)
  try {
    await mutateAsync(merged)
    addSuccess({ title: 'Profile saved successfully.' })
  } catch {
    addError({ title: 'Profile not saved.' })
  }
}
</script>

<template>
  <ModalContainer :width="1280">
    <ModalTitle>TOU PROFILE</ModalTitle>
    <ModalContent :minHeight="200" class="overflow-auto mt-0">
      <ContentLoader v-if="isLoading || touLoading" loading />
      <template v-else>
        <div class="d-flex justify-content-between align-items-center gap-3 w-100">
          <div class="d-flex align-items-center gap-2">
            <label for="utilityProvider" class="me-2 mb-0">Utility Provider</label>
            <FontAwesomeIcon
              class="tooltip-color ms-1 me-2"
              :icon="faQuestionCircle"
              v-tooltip="{
                placement: 'bottom',
                text: `Updating a TOU Profile that's linked <br>
                       to other Utility Providers will change <br>
                       all profiles it's connected to. <br>
                       Need help? Contact IT Support.`
              }"
            />
            <InputSelect
              :searchable="true"
              name="utilityProvider"
              :items="utilityProviderData ?? []"
              item-value="Id"
              item-text="Name"
              v-model:selected="utilityProvider"
              :display-limit="1"
              inline
            />
            <div class="d-flex align-items-center ms-2" v-if="linkedValue">
              <FontAwesomeIcon class="icon-style me-2" :icon="faQuestionCircle" />
              <span>({{ linkedValue }}) linked profiles.</span>
            </div>
          </div>

          <div class="btn-group btn-group-toggle" data-toggle="buttons">
            <label
              class="btn"
              :class="{ active: selectedState === PSOShort.Peak, 'btn-peak': selectedState === PSOShort.Peak }"
              @click="selectedState = PSOShort.Peak"
            >
              Peak
            </label>
            <label
              class="btn"
              :class="{ active: selectedState === PSOShort.Standard, 'btn-standard': selectedState === PSOShort.Standard }"
              @click="selectedState = PSOShort.Standard"
            >
              Standard
            </label>
            <label
              class="btn"
              :class="{ active: selectedState === PSOShort.OffPeak, 'btn-offpeak': selectedState === PSOShort.OffPeak }"
              @click="selectedState = PSOShort.OffPeak"
            >
              Off-Peak
            </label>
          </div>
        </div>

        <div class="d-flex flex-column align-items-end w-100">
          <TOUGrid
            v-if="hasSelectedProvider"
            :season="Season.Summer"
            :data="touData.Summer"
            :activeState="selectedState"
            @update:data="touData.Summer = $event"
          />
          <TOUGrid
            v-if="hasSelectedProvider"
            :season="Season.Winter"
            :data="touData.Winter"
            :activeState="selectedState"
            :headerHidden="true"
            @update:data="touData.Winter = $event"
          />
        </div>
      </template>
    </ModalContent>

    <ModalFooterConfirmation
      @reject="$emit('close')"
      @confirm="save()"
      :pending="false"
      :confirm-icon="faFloppyDisk"
      confirm-text="Save"
      reject-text="Cancel"
    />
  </ModalContainer>
</template>

<style scoped>
.btn {
  width: 80px;
  height: 23px;
  border: 1px solid #D1D1D1;
  border-radius: 4px;
  overflow: hidden;
  padding-top: 3px;
}
.btn-peak {
  background-color: #FD5624;
  color: white;
}
.btn-standard {
  background-color: #41C8DB;
  color: white;
}
.btn-offpeak {
  background-color: #103a48;
  color: white;
}
.icon-style {
  color: #FEE351;
}
</style>